import React, { useState } from 'react';
import { ArrowRight, Zap, Shield, Target, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

// Style to override global background inheritance for pricing card
const pricingCardStyle = `
  .pricing-card h3,
  .pricing-card p,
  .pricing-card div,
  .pricing-card span {
    background-color: transparent !important;
  }
`;

// New Logo Component
const CashyLogo: React.FC = () => (
  <div className="flex items-center gap-3">
    <div className="w-8 h-8 flex items-end justify-center gap-1 px-1 flex-shrink-0">
      {/* Three bars getting bigger from left to right */}
      <div className="w-1.5 bg-cashy-purple-400 rounded-sm" style={{ height: '14px' }}></div>
      <div className="w-1.5 bg-cashy-purple-500 rounded-sm" style={{ height: '18px' }}></div>
      <div className="w-1.5 bg-cashy-purple-600 rounded-sm" style={{ height: '22px' }}></div>
    </div>
    <div>
      <h1 className="text-2xl font-bold text-white">cashy.so</h1>
      <p className="text-xs text-gray-400 -mt-1">Inventory Intelligence</p>
    </div>
  </div>
);

export const LandingPage: React.FC = () => {
  const [selectedFeature, setSelectedFeature] = useState<string>('ai-powered');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const features = [
    { 
      id: 'ai-powered', 
      title: 'AI-Powered Analysis',
      description: 'Leverage our market-leading AI tools to get deep and meaningful insights into your business data. Our tool will act as your Co-Owner - advising you on what\'s working well, what\'s not and what actions you can take to improve your sales and profits.'
    },
    { 
      id: 'profit-loss', 
      title: 'Profit and Loss Breakdown',
      description: 'Understand your business\' profitability on sales, down to the finest detail.'
    },
    { 
      id: 'shipment', 
      title: 'Shipment Reconciliation',
      description: 'Get timely updates to your shipments into Amazon so that you can take quick action on any checking-in errors. Our AI system even advises what action to take when stock is missing.'
    },
    { 
      id: 'replenishable', 
      title: 'Replenishable Tracking',
      description: 'We provide a high-level view on your sales so you know exactly what products to replenish and when. Leave the guess work behind that other tools provide.'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <style>{pricingCardStyle}</style>
      {/* Header */}
      <header className="border-b border-gray-700 px-6 py-6 bg-gray-800">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <CashyLogo />
          <div className="flex items-center gap-6">
            <Link
              to="/login"
              className="text-gray-300 hover:text-white transition-colors font-medium px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="bg-cashy-purple-500 hover:bg-cashy-purple-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center space-y-12">
          {/* Main Headline */}
          <div className="space-y-8">
            <h1 className="text-5xl lg:text-7xl font-bold leading-tight text-white">
              <span className="bg-gradient-to-r from-cashy-purple-400 to-cashy-purple-600 bg-clip-text text-transparent">
                AI-led Data Analytics
              </span>
              <br />
              for Growing Amazon Sellers
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed max-w-4xl mx-auto">
              Transform your Amazon inventory data into actionable insights. 
              Get AI-powered recommendations so you can optimise your business performance.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link
              to="/signup"
              className="bg-cashy-purple-500 hover:bg-cashy-purple-600 text-white px-10 py-5 rounded-xl font-bold text-lg transition-all duration-200 flex items-center gap-3 shadow-2xl hover:shadow-purple-500/25 hover:scale-105"
            >
              Get Started Now
              <ArrowRight size={24} />
            </Link>
            <Link
              to="/login"
              className="border-2 border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white px-10 py-5 rounded-xl font-bold text-lg transition-all duration-200 hover:bg-gray-800"
            >
              Sign In
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center items-center gap-12 pt-8">
            <div className="flex items-center gap-3 text-gray-400">
              <Shield size={20} className="text-cashy-purple-400" />
              <span className="text-sm font-medium">Secure & Private</span>
            </div>
            <div className="flex items-center gap-3 text-gray-400">
              <Zap size={20} className="text-cashy-purple-400" />
              <span className="text-sm font-medium">Lightning Fast</span>
            </div>
            <div className="flex items-center gap-3 text-gray-400">
              <Target size={20} className="text-cashy-purple-400" />
              <span className="text-sm font-medium">AI-Powered</span>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-gray-800 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white">
              Every data point you need to truly understand how your business is performing.
            </h2>
          </div>

          <div className="bg-gray-900 rounded-2xl border-2 border-gray-700 p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              {/* Left Column - Feature Points */}
              <div className="space-y-4 lg:col-span-2">
                {features.map((feature) => (
                  <button
                    key={feature.id}
                    onClick={() => setSelectedFeature(feature.id)}
                    className={`w-full text-left p-6 rounded-xl transition-all duration-200 border-2 ${
                      selectedFeature === feature.id
                        ? 'bg-cashy-purple-500 text-white shadow-lg shadow-cashy-purple-500/30 border-cashy-purple-400'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white border-gray-600'
                    }`}
                  >
                    <h3 className="text-xl font-semibold">{feature.title}</h3>
                  </button>
                ))}
              </div>

              {/* Right Column - Content Area */}
              <div className="bg-gray-800 rounded-2xl p-8 border-2 border-gray-600 min-h-[400px] lg:col-span-3">
                {(() => {
                  const selected = features.find(f => f.id === selectedFeature);
                  return (
                    <div>
                      {/* Placeholder for screenshot */}
                      <div className="w-full h-64 bg-gray-700 rounded-lg border-2 border-dashed border-gray-600 flex items-center justify-center mb-6">
                        <p className="text-gray-500">Screenshot placeholder</p>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-4">
                        {selected?.title}
                      </h3>
                      <p className="text-gray-300 leading-relaxed text-lg">
                        {selected?.description}
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="bg-gray-900 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-gray-400">
              One plan. Everything included.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Pricing Card */}
            <div className="pricing-card bg-gradient-to-br from-cashy-purple-500 to-cashy-purple-600 rounded-2xl border-2 border-cashy-purple-400 p-8 lg:p-12 shadow-2xl shadow-cashy-purple-500/40 flex flex-col justify-center items-center text-center min-h-full">
              <h3 className="text-2xl font-semibold text-white mb-2">Premium</h3>
              <p className="text-white/90 mb-6">Full access to all features</p>
              <div className="mb-6">
                <span className="text-6xl font-bold text-white">£30.99</span>
                <span className="text-xl text-white/80 ml-2">/month</span>
              </div>
              <Link
                to="/signup"
                className="inline-flex items-center gap-3 bg-white hover:bg-gray-100 text-cashy-purple-600 px-10 py-4 rounded-xl font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
              >
                Get Started
                <ArrowRight size={20} />
              </Link>
            </div>

            {/* What's Included Panel */}
            <div className="bg-gray-800 rounded-2xl border border-gray-700 p-8 lg:p-12">
              <h3 className="text-2xl font-semibold text-white mb-6">What's included</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-cashy-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-300">AI-Powered Analysis & Insights</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-cashy-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-300">Profit and Loss Breakdown</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-cashy-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-300">Shipment Reconciliation</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-cashy-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-300">Replenishable Tracking</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-cashy-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-300">Inventory Insights & Analytics</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-cashy-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-300">Order Management & Reports</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-cashy-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-300">Unlimited data sync & storage</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-800 py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400">
              Everything you need to know about cashy.so
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: 'Question placeholder',
                answer: 'Answer placeholder text will go here.'
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden transition-all hover:border-gray-600"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
                >
                  <span className="text-lg font-semibold text-white pr-8">
                    {faq.question}
                  </span>
                  {openFaq === index ? (
                    <ChevronUp size={20} className="text-cashy-purple-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-300 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-700 px-6 py-6 bg-gray-800">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400 text-sm">
            © 2025 JP BUSINESS SOLUTIONS LTD (trading as Cashy). All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};